// SubstrateEmptyState — the absolute-zero fallback when even doctrine
// picks can't be generated because the substrate has no priced fixtures.
//
// This is the ONLY remaining empty screen in STRIKZA. It renders when:
//   1. The weekly substrate has status='empty' or fixtures_priced=0
//   2. The doctrine selectors returned 0 picks from both doctrines
//
// Uses generic design tokens (not OWL-specific) so it works on any page.

import type { CSSProperties } from "react";
import type { SubstrateWeekStatus } from "../doctrine/useSubstrateStatus.ts";

export interface SubstrateEmptyStateProps {
  week: SubstrateWeekStatus | null;
}

const card: CSSProperties = {
  background: "var(--bg-elev-1, #111)",
  border: "1px solid var(--border-faint, #2a2a2a)",
  borderRadius: 12,
  padding: "40px 32px",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
  marginTop: 24,
};

export function SubstrateEmptyState({ week }: SubstrateEmptyStateProps): JSX.Element {
  const discovered = week?.fixtures_discovered ?? 0;
  const priced = week?.fixtures_priced ?? 0;
  const weekStart = week?.week_start
    ? new Date(week.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "this week";
  const completedAt = week?.completed_at
    ? new Date(week.completed_at).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <article style={card}>
      <h2 style={{
        fontFamily: "var(--font-head, inherit)",
        fontSize: 24,
        letterSpacing: "0.04em",
        textTransform: "uppercase" as const,
        color: "var(--text-primary, #fff)",
        margin: 0,
      }}>
        No fixtures available
      </h2>
      <p style={{
        fontFamily: "var(--font-body, inherit)",
        fontSize: 14,
        lineHeight: 1.6,
        color: "var(--text-secondary, #999)",
        maxWidth: 520,
        margin: 0,
      }}>
        The weekly substrate scan found {discovered} fixture{discovered !== 1 ? "s" : ""} but
        could not price any of them into the fair price engine. This is a legitimate
        infrastructure state — it typically means the fixtures are too far out or
        league priors haven't accumulated enough data yet. STRIKZA never fabricates
        picks when the data isn't there.
      </p>
      <div style={{
        fontFamily: "var(--font-mono, monospace)",
        fontSize: 11,
        color: "var(--text-tertiary, #666)",
        letterSpacing: "0.04em",
        marginTop: 8,
      }}>
        Week of {weekStart} · {discovered} discovered · {priced} priced
        {completedAt ? ` · Last scan: ${completedAt}` : ""}
      </div>
    </article>
  );
}
